import {TargetGroup} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/target_group.types';
import {GetHeaderBulkRequest} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/service.types';
import {
  PageTypes,
  useCurrentPageType,
  useParams,
  useQueryParams,
} from '@aboutyou/router';
import {getEntityIdFromRouteParam} from '@aboutyou/utils';
import {useMemo} from 'react';
import {convertGenderToTargetGroup} from '../../../shared-deprecated/useSelectedTargetGroup';

export const useEntityIdentifier = () => {
  const currentPageType = useCurrentPageType();
  const genderParamIdentifier = useGenderParamIdentifier();

  const pathParams = useParams();
  const queryParams = useQueryParams();

  return useMemo(() => {
    return getEntityIdentifier({
      currentPageType,
      genderParamIdentifier,
      pathParams,
      queryParams,
    });
  }, [currentPageType, genderParamIdentifier, pathParams, queryParams]);
};

// eslint-disable-next-line complexity
export const getEntityIdentifier = ({
  currentPageType,
  genderParamIdentifier,
  pathParams,
  queryParams,
}: {
  currentPageType: PageTypes | undefined;
  genderParamIdentifier: TargetGroup | undefined;
  pathParams: Record<string, string | undefined>;
  queryParams: URLSearchParams;
}): GetHeaderBulkRequest['identifier'] => {
  if (currentPageType === 'home' || currentPageType === 'welcome') {
    return {
      $case: 'browse' as const,
      browse: {targetGroup: genderParamIdentifier},
    };
  }

  if (currentPageType === 'searchResults') {
    return {
      $case: 'search' as const,
      search: {targetGroup: genderParamIdentifier},
    };
  }

  if (currentPageType === 'seoStream') {
    return {
      $case: 'seo' as const,
      seo: {targetGroup: genderParamIdentifier},
    };
  }

  if (currentPageType === 'category') {
    const id = getEntityIdFromRouteParam(pathParams.categoryId);

    if (id) {
      return {
        $case: 'category' as const,
        category: {identifier: {$case: 'id', id}},
      };
    }
  }

  if (
    currentPageType === 'articleDetail' ||
    currentPageType === 'similarProducts' ||
    currentPageType === 'productReviews'
  ) {
    const productId = getEntityIdFromRouteParam(pathParams.prefixedProductId);
    if (productId) {
      return {
        $case: 'product' as const,
        product: {identifier: {$case: 'id', id: productId}},
      };
    }
  }

  if (currentPageType === 'brandShop' && pathParams.brandId) {
    const brandIdParam = Number(pathParams.brandId);

    const isBrandHub = queryParams.get('cluster') === 'true';
    const categoryParam = queryParams.get('category');
    const categoryIdParam = categoryParam ? Number(categoryParam) : undefined;

    if (brandIdParam) {
      return {
        $case: 'brand' as const,
        brand: {
          brand: {identifier: {$case: 'id', id: brandIdParam}},
          category: categoryIdParam ? {id: categoryIdParam} : undefined,
          isBrandHub,
        },
      };
    }
  }

  if (currentPageType === 'story') {
    const storyId = getEntityIdFromRouteParam(pathParams.prefixedStoryId);

    if (storyId) {
      return {
        $case: 'story' as const,
        story: {identifier: {$case: 'id', id: storyId}},
      };
    }
  }

  if (currentPageType === 'outfitDetail') {
    const outfitId = getEntityIdFromRouteParam(pathParams.prefixedOutfitId);

    if (outfitId) {
      return {
        $case: 'outfit' as const,
        outfit: {identifier: {$case: 'id', id: outfitId}},
      };
    }
  }
};

const useGenderParamIdentifier = () => {
  const queryParams = useQueryParams();
  const paramGender = queryParams.get('gender');

  if (paramGender) {
    return convertGenderToTargetGroup({
      gender: paramGender,
      defaultToFemale: false,
    });
  }
};
